"use client";

import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  Suspense,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { Toaster } from "sonner";
import { useTheme } from "@mui/material/styles";
import { TOAST_CONFIG, getToastStyle } from "@/utils/theme/ToastConfig";
import { Box, useMediaQuery, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { useWindowSize } from "react-use";

const Confetti = React.lazy(() => import("react-confetti"));
const VanityModal = React.lazy(() =>
  import("@/app/components/modals/vanity/VanityModal"),
);
const ReviewSubmissionModal = React.lazy(() =>
  import("@/app/components/modals/review/ReviewSubmissionModal"),
);
const ScheduledMeetingDialog = React.lazy(() =>
  import("@/app/components/modals/schedule/ScheduledMeetingDialog"),
);

import { store } from "@/store/store";
import {
  fetchUserData,
  markEnrichmentComplete,
  requestContactEmailConfirmation,
  setLinkedInCodeVerified,
  setRequiresManualInput,
  setUnderReview,
  triggerPostEnrichmentReplay,
  saveUserData,
} from "@/store/apps/userData/UserDataSlice";
import {
  hideAcceptedConfirmation,
  hideScheduledConfirmation,
} from "@/store/apps/meetings/MeetingDataSlice";

import { usePusherEnrichment } from "@/lib/hooks/usePusherEnrichment";
import { getPusherClient } from "@/lib/services/pusher-client";

import EnrichmentNotifier from "@/app/components/notifier/EnrichmentNotifier";
import UpdateNotifier from "@/app/components/notifier/UpdateNotifier";
import InstallNotifier from "@/app/components/notifier/InstallNotifier";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";
import EmailNotifier from "@/app/components/notifier/EmailNotifier";
import PendingEmailNotifier from "@/app/components/notifier/PendingEmailNotifier";
import UnverifiedEmailNotifier from "@/app/components/notifier/UnverifiedEmailNotifier";

import "@/app/global.css";
import Header from "@/app/components/dashboards/layout/header/Header";
import Sidebar from "@/app/components/dashboards/layout/sidebar/Sidebar";
import Footer from "@/app/components/Footer";
import {
  listenToFirestoreMeetings,
  fetchMeetings,
  setupHybridMeetingUpdates,
} from "@/store/apps/meetings/MeetingThunks";
import { useSession } from "next-auth/react";
import ActionNotifier from "@/app/components/notifier/ActionNotifier";
import ExtensionNotifier from "@/app/components/notifier/ExtensionNotifier";
import MeetingLaunchWatchdog from "@/app/components/notifier/MeetingLaunchWatchdog";
import { useTimezone } from "@/app/utils/TimezoneContext";
import useVanityModal from "@/app/components/modals/vanity/useVanityModal";

function DashboardLayout({ children, onVerificationHandoffChange = () => {} }) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const theme = useTheme();

  const { data: session, status } = useSession();

  const dispatch = useDispatch();
  const scrollContainerRef = useRef(null);
  const { width, height } = useWindowSize();
  const { timezone: userTimezone } = useTimezone();

  const [isInitializing, setIsInitializing] = useState(true);
  const [enrichmentActive, setEnrichmentActive] = useState(false);
  const [emailActive, setEmailActive] = useState(false);
  const [pendingEmailActive, setPendingEmailActive] = useState(false);
  const [unverifiedEmailActive, setUnverifiedEmailActive] = useState(false); // NEW
  const [updateActive, setUpdateActive] = useState(false);
  const [
    showAvailabilityClearedDialog,
    setShowAvailabilityClearedDialog,
  ] = useState(false);
  const [actionActive, setActionActive] = useState(false);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const showVideoFooter = useMediaQuery(theme.breakpoints.up("sm"));

  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewMeetingData, setReviewMeetingData] = useState(null);
  const [reviewModalLoading, setReviewModalLoading] = useState(false);

  const {
    modalOpen: vanityModalOpen,
    openModal: openVanityModal,
    handleCloseModal: handleVanityClose,
  } = useVanityModal();

  const handleVanitySuccess = async ({ preserveEnrichment = false } = {}) => {
    handleVanityClose();
    dispatch(setLinkedInCodeVerified(true));
    dispatch(setRequiresManualInput(false));
    await dispatch(fetchUserData(true, { silent: true }));
    if (!preserveEnrichment) {
      dispatch(markEnrichmentComplete());
    }
    // If there was a pending meeting request in the URL, trigger a re-fetch
    // so HubClient can process it now that enrichment is complete.
    if (searchParams.get("id")) {
      dispatch(triggerPostEnrichmentReplay());
    } else {
      router.refresh();
    }
  };

  const customizer = useSelector((state) => state.customizer);
  const profile = useSelector((state) => state.user?.profile);
  const availabilityData = useSelector((state) => state.availability.data);
  const instantBookingEnabled = Boolean(profile?.preferences?.instant_booking);
  const hasAvailability = (() => {
    const times = availabilityData?.times;
    if (!times || typeof times !== "object") return false;
    return Object.values(times).some(
      (daySlots) =>
        Array.isArray(daySlots) &&
        daySlots.some(
          (slot) =>
            slot && typeof slot === "object" && Object.keys(slot).length > 0,
        ),
    );
  })();
  const prevHasAvailabilityRef = useRef(null);

  const {
    showAcceptedDialog,
    acceptedSeekerName,
    showScheduledDialog,
    scheduledMeetingDetails,
  } = useSelector((state) => state.meetings);

  const handleCloseConfirmation = () => {
    dispatch(hideAcceptedConfirmation());
  };

  const handleCloseScheduledConfirmation = () => {
    dispatch(hideScheduledConfirmation());
  };

  useEffect(() => {
    const reviewMeetingId = searchParams.get("reviewMeeting");

    if (reviewMeetingId && session?.user?.sub) {
      fetchMeetingForReview(reviewMeetingId);

      const newUrl = new URL(window.location);
      newUrl.searchParams.delete("reviewMeeting");
      router.replace(newUrl.pathname + newUrl.search, { scroll: false });
    }
  }, [searchParams, session?.user?.sub]);

  const fetchMeetingForReview = async (meetingId) => {
    setReviewModalLoading(true);

    try {
      const checkReview = await fetch(
        `/api/reviews/check?meetingId=${meetingId}`,
      );
      const reviewCheck = await checkReview.json();

      if (reviewCheck.exists) {
        setReviewModalLoading(false);
        return;
      }

      const response = await fetch(`/api/meeting/${meetingId}`);
      const data = await response.json();

      if (response.ok && data.success && data.meeting) {
        const meeting = data.meeting;

        const isSeeker = meeting.seeker_sub === session.user.sub;

        const otherPerson = isSeeker ? meeting.sharer : meeting.seeker;

        if (!otherPerson || !otherPerson.given_name) {
          setReviewModalLoading(false);
          return;
        }

        const fullName = `${otherPerson.given_name} ${otherPerson.family_name ||
          ""}`.trim();

        setReviewMeetingData({
          meetingId: meetingId,
          sharerId: otherPerson.sub,
          sharerName: fullName,
        });

        setReviewModalOpen(true);
      }
    } catch (error) {
    } finally {
      setReviewModalLoading(false);
    }
  };

  const handleReviewSubmitted = () => {
    setReviewModalOpen(false);
    setReviewMeetingData(null);
  };

  useEffect(() => {
    const updateStatusBarColor = () => {
      const isDarkMode = customizer.activeMode !== "light";
      const statusBarColor = isDarkMode ? "#1A2129" : "#fafbfb";

      let themeColorMeta = document.querySelector('meta[name="theme-color"]');
      if (!themeColorMeta) {
        themeColorMeta = document.createElement("meta");
        themeColorMeta.name = "theme-color";
        document.head.appendChild(themeColorMeta);
      }
      themeColorMeta.content = statusBarColor;

      let statusBarMeta = document.querySelector(
        'meta[name="apple-mobile-web-app-status-bar-style"]',
      );
      if (!statusBarMeta) {
        statusBarMeta = document.createElement("meta");
        statusBarMeta.name = "apple-mobile-web-app-status-bar-style";
        document.head.appendChild(statusBarMeta);
      }
      statusBarMeta.content = isDarkMode ? "black-translucent" : "default";

      let viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        const currentContent = viewportMeta.content;
        if (!currentContent.includes("viewport-fit")) {
          viewportMeta.content = `${currentContent}, viewport-fit=cover`;
        }
      }
    };

    updateStatusBarColor();
  }, [customizer.activeMode]);

  const meetingsInitialized = useRef(false);
  const videoEnrichmentKickoffRef = useRef(false);

  const handleEnrichmentComplete = useCallback(
    async (data) => {
      dispatch(markEnrichmentComplete());
      dispatch(fetchUserData(true, { silent: true }));
      if (searchParams.get("id")) {
        dispatch(triggerPostEnrichmentReplay());
      }

      if (!meetingsInitialized.current && session?.user?.sub) {
        meetingsInitialized.current = true;

        const pollForMeetingSetup = async () => {
          const maxWaitTime = 20000;
          const pollInterval = 1000;
          const startTime = Date.now();

          while (Date.now() - startTime < maxWaitTime) {
            try {
              const response = await fetch("/api/meeting/get-meetings", {
                credentials: "include",
              });
              const meetingsData = await response.json();

              if (meetingsData.success && meetingsData.meetingsCount > 0) {
                dispatch(listenToFirestoreMeetings(session.user.sub));
                return;
              }
            } catch (error) {
              console.error("POLL] Error during polling:", error.message);
            }

            await new Promise((resolve) => setTimeout(resolve, pollInterval));
          }

          dispatch(listenToFirestoreMeetings(session.user.sub));
        };

        pollForMeetingSetup();
      }
    },
    [dispatch, searchParams, session?.user?.sub],
  );

  usePusherEnrichment(handleEnrichmentComplete);

  useEffect(() => {
    let cleanup;
    let isSubscribed = true;

    const initializeEverything = async () => {
      if (status === "loading") {
        return;
      }

      if (status !== "authenticated" || !session?.user?.sub) {
        if (isSubscribed) {
          setIsInitializing(false);
        }
        return;
      }
      // Fire meetings fetch immediately — it only needs session.sub which is
      // already available, not anything from fetchUserData. Running both in
      // parallel saves the full REST round-trip (~300ms) for returning users.
      // Safe for new users too: returns [] or an existing draft meeting.
      dispatch(fetchMeetings());

      await dispatch(fetchUserData());

      if (isSubscribed) {
        const state = store.getState();
        setIsInitializing(false);

        const userNeedsEnrichment = state.user.needsEnrichment;
        const userEnrichmentStatus = state.user.enrichmentStatus;

        if (
          pathname?.startsWith("/video") &&
          userNeedsEnrichment === true &&
          !videoEnrichmentKickoffRef.current
        ) {
          videoEnrichmentKickoffRef.current = true;
          fetch("/api/hub", { credentials: "include" })
            .then(() => dispatch(fetchUserData(true, { silent: true })))
            .catch(() => {});
        }

        if (!userNeedsEnrichment && userEnrichmentStatus === "completed") {
          if (!meetingsInitialized.current) {
            meetingsInitialized.current = true;
            // Meetings REST data already loaded above — only start Pusher now
            // to avoid a redundant second fetch.
            const result = dispatch(
              setupHybridMeetingUpdates(session.user.sub),
            );
            if (typeof result === "function") {
              cleanup = result;
            }
          }
        }
      }
    };

    initializeEverything();

    return () => {
      isSubscribed = false;
      if (cleanup && typeof cleanup === "function") {
        cleanup();
      }
    };
  }, [session?.user?.sub, status, dispatch, pathname]);

  const userDataState = useSelector((state) => state.user);
  const reduxIsUnderReview = useSelector((state) => state.user.isUnderReview);
  const reviewEmailConfirmed =
    userDataState?.profile?.email_acknowledged === true;
  const isMessageSignup = ["google", "email"].includes(
    session?.user?.signupProvider,
  );
  const messageSignupNeedsVerification =
    isMessageSignup &&
    userDataState?.hasFetched === true &&
    userDataState?.isInitializationComplete !== true;

  const vanityModalNeeded =
    (userDataState?.requiresManualInput === true ||
      messageSignupNeedsVerification) &&
    userDataState?.linkedinCodeVerified !== true &&
    userDataState?.enrichmentStatus !== "completed" &&
    (isMessageSignup || userDataState?.enrichmentStatus !== "processing") &&
    !userDataState?.isProcessingBooking;

  const messageSignupHandoffActive =
    isMessageSignup &&
    userDataState?.linkedinCodeVerified !== true &&
    userDataState?.isInitializationComplete !== true &&
    reduxIsUnderReview !== true;

  const verificationHandoffActive =
    isInitializing ||
    messageSignupHandoffActive ||
    vanityModalNeeded ||
    vanityModalOpen;

  useEffect(() => {
    onVerificationHandoffChange(verificationHandoffActive);
  }, [onVerificationHandoffChange, verificationHandoffActive]);

  useEffect(() => {
    if (vanityModalNeeded && !vanityModalOpen && userDataState?.profile) {
      const timer = setTimeout(() => openVanityModal(), 0);
      return () => clearTimeout(timer);
    }
  }, [
    vanityModalNeeded,
    vanityModalOpen,
    openVanityModal,
    userDataState?.profile,
  ]);

  // When admin approves a LinkedIn review, close the VanityModal review screen
  // and refresh user data immediately — no manual page reload needed.
  useEffect(() => {
    if (!reduxIsUnderReview || !session?.user?.sub) return;

    const pusherClient = getPusherClient();
    if (!pusherClient) return;

    const channelName = `private-user-${session.user.sub}`;
    const channel = pusherClient.subscribe(channelName);

    const handleProfileApproved = () => {
      dispatch(setUnderReview(false));
      dispatch(markEnrichmentComplete());
      dispatch(fetchUserData(true, { silent: true }));
      handleVanityClose();
    };

    channel.bind("profile-approved", handleProfileApproved);

    return () => {
      channel.unbind("profile-approved", handleProfileApproved);
      pusherClient.unsubscribe(channelName);
    };
  }, [reduxIsUnderReview, session?.user?.sub, dispatch, handleVanityClose]);

  const hasHigherPriorityNotifications = enrichmentActive || vanityModalNeeded;

  const hasEmailOrHigherNotifications =
    enrichmentActive || vanityModalNeeded || emailActive;

  const hasPendingEmailOrHigherNotifications =
    enrichmentActive || vanityModalNeeded || emailActive || pendingEmailActive;

  const hasUnverifiedEmailOrHigherNotifications =
    enrichmentActive ||
    vanityModalNeeded ||
    emailActive ||
    pendingEmailActive ||
    unverifiedEmailActive;

  const hasUpdateOrHigherNotifications =
    enrichmentActive ||
    vanityModalNeeded ||
    emailActive ||
    pendingEmailActive ||
    unverifiedEmailActive ||
    updateActive;

  const hasActionOrHigherNotifications =
    enrichmentActive ||
    vanityModalNeeded ||
    emailActive ||
    pendingEmailActive ||
    unverifiedEmailActive ||
    updateActive ||
    actionActive;

  useEffect(() => {
    const handleScroll = () => {};

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle(
      "dark-mode",
      customizer.activeMode !== "light",
    );
  }, [customizer.activeMode]);

  // Auto-disable instant booking when user clears all availability
  useEffect(() => {
    if (
      prevHasAvailabilityRef.current === true &&
      !hasAvailability &&
      instantBookingEnabled
    ) {
      dispatch(
        saveUserData({
          preferences: { ...profile?.preferences, instant_booking: false },
          silentUpdate: true,
        }),
      )
        .then(() => {
          setShowAvailabilityClearedDialog(true);
        })
        .catch(() => {});
    }
    prevHasAvailabilityRef.current = hasAvailability;
  }, [hasAvailability]);

  const renderContent = () => (
    <Grid
      id="main-scroll-container"
      container
      className="page-wrapper hide-scrollbar"
      ref={scrollContainerRef}
      sx={{
        flexGrow: 1,
        overflow: "auto",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        msOverflowStyle: "none",
        scrollbarWidth: "none",
        ...(customizer.isCollapse && {
          [theme.breakpoints.up("lg")]: {
            ml: `${customizer.MiniSidebarWidth}px`,
          },
        }),
        display: "flex",
        flexDirection: "column",
        width: "100%",
        backgroundColor: "inherit",
        position: "relative",
        paddingTop: `calc(${
          customizer.TopbarHeight
        }px + env(safe-area-inset-top) + ${isDesktop ? "24px" : "16px"})`,
        pr: isDesktop ? 4 : 0,
        justifyContent: "flex-start",
      }}
    >
      {children}
    </Grid>
  );

  return (
    <Grid
      container
      direction="column"
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <EnrichmentNotifier onActiveChange={setEnrichmentActive} />
      <EmailNotifier
        hasOtherNotifications={hasHigherPriorityNotifications}
        onActiveChange={setEmailActive}
      />
      <PendingEmailNotifier
        hasOtherNotifications={hasEmailOrHigherNotifications}
        onActiveChange={setPendingEmailActive}
      />
      <UnverifiedEmailNotifier
        hasOtherNotifications={hasPendingEmailOrHigherNotifications}
        onActiveChange={setUnverifiedEmailActive}
      />
      <UpdateNotifier
        hasOtherNotifications={hasUnverifiedEmailOrHigherNotifications}
        onActiveChange={setUpdateActive}
      />
      <ActionNotifier
        hasOtherNotifications={hasUpdateOrHigherNotifications}
        onActiveChange={setActionActive}
      />
      <MeetingLaunchWatchdog />
      <InstallNotifier hasOtherNotifications={hasActionOrHigherNotifications} />

      {vanityModalNeeded && (
        <Box
          aria-hidden="true"
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.28)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            pointerEvents: "auto",
            zIndex: 1200,
          }}
        />
      )}

      <Suspense fallback={null}>
        <VanityModal
          open={vanityModalOpen || vanityModalNeeded}
          handleClose={handleVanityClose}
          onSuccess={handleVanitySuccess}
          hasPendingRequest={Boolean(searchParams.get("id"))}
        />
      </Suspense>

      <ConfirmDialog
        open={reduxIsUnderReview && pathname !== "/"}
        onClose={() => {}}
        onConfirm={() => dispatch(requestContactEmailConfirmation())}
        title="Profile Under Review"
        body="We're reviewing your LinkedIn profile. We'll email you within 48 hours."
        showConfirmButton={!reviewEmailConfirmed}
        showIgnoreButton={false}
        showCloseIcon={false}
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
        confirmButtonText="Confirm contact email"
      />

      {reviewMeetingData && (
        <Suspense fallback={null}>
          <ReviewSubmissionModal
            open={reviewModalOpen}
            onClose={() => {
              setReviewModalOpen(false);
              setReviewMeetingData(null);
            }}
            sharerName={reviewMeetingData.sharerName}
            sharerId={reviewMeetingData.sharerId}
            meetingId={reviewMeetingData.meetingId}
            onReviewSubmitted={handleReviewSubmitted}
            meetingSummary={
              reviewMeetingData.video_call?.summary?.content || null
            }
          />
        </Suspense>
      )}

      <Header />
      <Grid
        sx={{
          display: "flex",
          flexGrow: 1,
          width: "100%",
          overflow: "hidden",
          height: "auto",
        }}
      >
        {isDesktop && (
          <Box
            sx={{
              width: customizer.SidebarWidth,
              flexShrink: 0,
            }}
          >
            <Sidebar />
          </Box>
        )}
        {renderContent()}
      </Grid>
      {(!pathname.includes("/video") || showVideoFooter) && <Footer />}

      {!pathname.includes("/video") && <ExtensionNotifier />}

      <ConfirmDialog
        open={showAvailabilityClearedDialog}
        onClose={() => setShowAvailabilityClearedDialog(false)}
        onConfirm={() => {
          setShowAvailabilityClearedDialog(false);
          router.push("/availability");
        }}
        onIgnore={() => setShowAvailabilityClearedDialog(false)}
        title="Instant Booking Turned Off"
        subtitle=""
        body="You removed all your availability, so instant booking was automatically disabled. Set your hours again to re-enable it."
        confirmButtonText="Set Availability"
        ignoreButtonText="Got It"
        showConfirmButton={true}
        showIgnoreButton={true}
        modalStyle="toast"
        confirmButtonVariant="text"
      />

      <ConfirmDialog
        open={showAcceptedDialog}
        onClose={handleCloseConfirmation}
        onConfirm={handleCloseConfirmation}
        title="Request accepted!"
        body={`You've accepted the meeting request. Once ${acceptedSeekerName} picks a time from your available slots, you'll get an email with the meeting details.`}
        showIgnoreButton={false}
        showCloseIcon={true}
        showConfirmButton={false}
        modalStyle="toast"
        titleSx={{ fontWeight: 700, fontSize: "1.5rem" }}
      />

      <Suspense fallback={null}>
        <ScheduledMeetingDialog
          open={showScheduledDialog}
          onClose={handleCloseScheduledConfirmation}
          scheduledMeetingDetails={scheduledMeetingDetails}
          profile={profile}
          userTimezone={userTimezone}
        />
      </Suspense>

      {showScheduledDialog && (
        <Suspense fallback={null}>
          <Confetti
            width={width}
            height={height}
            recycle={false}
            opacity={0.8}
            gravity={0.3}
            colors={[theme.palette.primary.dark, theme.palette.brand?.linkedin]}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              zIndex: 99999,
              pointerEvents: "none",
            }}
          />
        </Suspense>
      )}

      <Toaster
        position={TOAST_CONFIG.getPosition(!isDesktop)}
        closeButton={TOAST_CONFIG.closeButton}
        duration={TOAST_CONFIG.duration}
        expand={false}
        visibleToasts={3}
        toastOptions={{
          style: getToastStyle(theme),
          classNames: {
            toast: "custom-toast",
            title: "custom-toast-title",
            description: "custom-toast-description",
          },
        }}
        style={{
          marginRight: isDesktop ? TOAST_CONFIG.marginRight : "auto",
          marginLeft: !isDesktop ? "auto" : undefined,
          ...(!isDesktop && {
            top: "calc(env(safe-area-inset-top) + 8px)",
          }),
        }}
      />
    </Grid>
  );
}

export default DashboardLayout;
